// Billing Group editor for the Billing Review detail modal.
// Lets the user set the parent of the current client — any firm client the server's parent search
// offers (not only the user's own book). Server enforces the three rules (self-parent-with-children,
// parent-not-child, no-cycles).

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import ClientCodeAutocomplete from './ClientCodeAutocomplete';
import PendingTag from './PendingTag';
import { submitChange, wasWithdrawn, withPending, withoutPending, revertsPending } from './billingChangeApi';

// Everyone in the billing group headed by `code`, with this client highlighted as the pending addition.
// Looks the group up on the server (debounced; stale answers dropped); nothing shows for self / an unknown code.
function GroupPreview({ code, client, fetchGroup }) {
  const [group, setGroup] = useState(null);
  const fetchRef = useRef(fetchGroup);       // the caller passes a new function every render
  fetchRef.current = fetchGroup;
  const target = String(code || '').trim();
  const isSelf = target.toLowerCase() === client.clientCode.toLowerCase();

  useEffect(() => {
    setGroup(null);
    if (!fetchRef.current || !target || isSelf) return undefined;
    let stale = false;
    const t = setTimeout(async () => {
      try {
        const g = await fetchRef.current(target);
        if (!stale) setGroup(g || null);
      } catch { if (!stale) setGroup(null); }
    }, 350);
    return () => { stale = true; clearTimeout(t); };
  }, [target, isSelf]);

  if (!group) return null;
  const rows = [
    { code: group.code, name: group.name, parent: true },
    ...group.members,
    { code: client.clientCode, name: client.clientName, me: true },
  ].sort((a, b) => (a.parent ? -1 : b.parent ? 1 : a.code.localeCompare(b.code)));
  return (
    <div className="bg-preview">
      <div className="bg-preview-title">
        Billing group {group.code}{group.name ? ` — ${group.name}` : ''} · {rows.length - 1} child{rows.length - 1 === 1 ? '' : 'ren'} after this change
      </div>
      <ul className="bg-preview-list">
        {rows.map(r => (
          <li key={r.code} className={r.me ? 'bg-preview-me' : ''}>
            <strong>{r.code}</strong>{r.name ? ` — ${r.name}` : ''}
            {r.parent && <span className="bg-preview-tag">parent</span>}
            {r.me && <span className="bg-preview-tag bg-preview-tag--pending">pending</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BillingGroupEditor({
  client, period, readOnly, searchParents, fetchGroup, onOptimisticUpdate, onChanged, pending, onWithdraw,
}) {
  const { email } = useAuth();
  const bg = client.billingGroup;
  const currentParent = bg?.parentCode || client.clientCode; // default self if no mapping
  const isParentOfOthers = !!bg?.isParent;
  const children = (bg?.children || []).filter(c => c !== client.clientCode);
  const violation = !!bg?.violation;

  const [editing, setEditing] = useState(false);
  const [parent, setParent] = useState(currentParent);
  const [saving, setSaving] = useState(false);

  async function save() {
    const typed = String(parent || '').trim();
    if (!typed) { toast.error('Parent code is required.'); return; }

    // The rules the server enforces that don't need a lookup are checked first so a bad pick never
    // round-trips; "is it a real, eligible client" comes from the same parent search the dropdown uses.
    const isSelfPick = typed.toLowerCase() === client.clientCode.toLowerCase();
    if (mustSelf && !isSelfPick) {
      toast.error(`${client.clientCode} has children, so it must remain its own billing parent.`);
      return;
    }
    let picked = isSelfPick ? client.clientCode : null;
    if (!picked) {
      try {
        const found = await searchParents?.(typed);
        picked = (found || []).find(o => String(o.code).toLowerCase() === typed.toLowerCase())?.code || null;
      } catch (err) {
        toast.error(`Couldn't check "${typed}": ${err.message}`, { autoClose: 10000 });
        return;
      }
    }
    if (!picked) {
      toast.error(`"${typed}" isn't an eligible parent. A parent must be an active client that isn't already a child of another billing group.`);
      return;
    }
    const newParent = picked;

    const snapshotBg = client.billingGroup;
    const snapshotPending = client.pending || [];
    const body = { section: 'billing_group', fieldKey: 'billing_group', payload: { parentCode: newParent } };
    // Choosing the current (live) parent again just takes the open request back.
    const undo = revertsPending(pending, body, email);
    // Optimistic: just update parentCode (and the "Pending approval" tag). Children/violation will
    // sync on next reload.
    onOptimisticUpdate?.(c => {
      const next = {
        ...c,
        billingGroup: c.billingGroup
          ? { ...c.billingGroup, parentCode: newParent }
          : {
              parentCode: newParent,
              children: [c.clientCode],
              isParent: false,
              isSelfOnly: newParent === c.clientCode,
              violation: false,
              appendClientName: false,
            },
      };
      return undo
        ? withoutPending(next, body.section, body.fieldKey, body.payload)
        : withPending(next, { ...body, summary: `Billing parent → ${newParent}`, email });
    });
    setEditing(false);
    setSaving(true);

    try {
      // Staged: nothing is live until the Billing Team approves. Append Client Name is its
      // own setting (Misc), so only the parent is sent here.
      const out = await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success(wasWithdrawn(out)
        ? `Billing group change for ${client.clientCode} withdrawn.`
        : `Billing group change for ${client.clientCode} → ${newParent} submitted for Billing Team approval.`);
      onChanged?.();
    } catch (err) {
      onOptimisticUpdate?.(c => ({ ...c, billingGroup: snapshotBg, pending: snapshotPending }));
      toast.error(`Save failed: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSaving(false);
    }
  }

  // When the client has children, the parent is locked to self (RULE 1).
  const mustSelf = isParentOfOthers;

  if (!editing) {
    // Treat "no entry in the blob" as self-parented — display the same way a
    // client with { parentCode === clientCode } is shown.
    const parentIsSelf = currentParent === client.clientCode;
    return (
      <div className="bg-editor">
        <div className="detail-row">
          <span className="detail-label">Parent:</span> {currentParent}
          {!parentIsSelf && bg?.parentName ? ` — ${bg.parentName}` : ''}
          {parentIsSelf && <span className="bg-self-tag">Self</span>}
          <PendingTag change={pending} onWithdraw={onWithdraw} />
        </div>
        {pending && !parentIsSelf && <GroupPreview code={currentParent} client={client} fetchGroup={fetchGroup} />}
        {isParentOfOthers && (
          <div className="detail-row">
            <span className="detail-label">Children:</span>{' '}
            <span
              className="bg-children-list"
              title={children.length ? children.join(', ') : 'None'}
            >
              {children.join(', ') || 'None'}
            </span>
          </div>
        )}
        {violation && (
          <div className="detail-violation">
            <i className="fas fa-exclamation-triangle" /> This client is a parent of other clients
            but also appears as a child under a different parent. No grandparent/grandchild
            relationships allowed.
          </div>
        )}

        {!readOnly && (
          <button className="cs-btn cs-btn--add" onClick={() => setEditing(true)}>
            <i className="fas fa-edit" /> Change Parent
          </button>
        )}
      </div>
    );
  }

  // Edit mode
  return (
    <div className="bg-editor bg-editor--editing">
      <div className="bg-edit-row">
        <label className="detail-label">New parent code:</label>
        <ClientCodeAutocomplete
          value={parent}
          onChange={setParent}
          search={searchParents}
          emptyMessage="No eligible parent matches. A parent can be any active client that isn't already a child of another billing group."
          disabled={mustSelf || saving}
          placeholder={client.clientCode}
          autoFocus
          inputClassName="cs-input bg-parent-input"
        />
        <button
          type="button"
          className="cs-btn"
          disabled={saving}
          onClick={() => setParent(client.clientCode)}
          title="Set parent to self (standalone)"
        >
          Self
        </button>
      </div>

      {!mustSelf && <GroupPreview code={parent} client={client} fetchGroup={fetchGroup} />}

      {mustSelf && (
        <div className="bg-lock-note">
          <i className="fas fa-lock" /> This client has children ({children.length}), so it must
          remain its own parent.
        </div>
      )}

      <div className="bg-edit-actions">
        <button className="cs-btn cs-btn--save" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="cs-btn" disabled={saving}
                onClick={() => { setEditing(false); setParent(currentParent); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
